import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, X, Loader2, Smartphone, Monitor, Download, Copy, Share, Settings, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { detectDevice, enablePush, getSubscription, canInstall, promptInstall } from "@/lib/push";

const KEY = "pushcard_snooze_until";
const SNOOZE_DAYS = 7;
const snoozed = () => Number(localStorage.getItem(KEY) || 0) > Date.now();

const copyLink = () => { navigator.clipboard?.writeText(window.location.origin + "/feed"); toast.success("Link kopiert – jetzt im Browser einfügen"); };
const chromeIntent = () => `intent://${window.location.host}/feed#Intent;scheme=https;package=com.android.chrome;end`;

const TEXT = {
  "ios-install": ["Push aufs iPhone holen", "Apple erlaubt Push nur, wenn itzPlay auf dem Home-Bildschirm liegt. Tippe in Safari auf Teilen → „Zum Home-Bildschirm“ und öffne die App dann dort."],
  "ios-ready": ["Push-Benachrichtigungen aktivieren", "Verpasse keine Kommentare, Aufgaben und Freundschaftsanfragen mehr."],
  "ios-inapp": ["Bitte in Safari öffnen", "In diesem Browser kann dein iPhone keine Push-Nachrichten empfangen. Kopiere den Link und öffne ihn in Safari."],
  "android-inapp": ["Bitte in Chrome öffnen", "In diesem App-Browser funktionieren Push-Nachrichten nicht. Öffne itzPlay in Chrome."],
  android: ["Push-Benachrichtigungen aktivieren", "Ein Tipp – und du bekommst Kommentare, Aufgaben und Anfragen direkt aufs Handy."],
  desktop: ["Push-Benachrichtigungen aktivieren", "Erhalte Mitteilungen direkt auf deinem Computer, auch wenn der Tab geschlossen ist."],
  denied: ["Push ist blockiert", "Du hast Benachrichtigungen abgelehnt. Erlaube sie in den Browser- bzw. Handy-Einstellungen für diese Seite und lade neu."],
};

const cta = "inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full font-bold text-sm transition-transform active:scale-95";

function Actions({ d, busy, onEnable, installable, onInstall }) {
  if (d.kind === "ios-install") return <Link to="/push" data-testid="pushcard-ios-guide" className={`${cta} pink-gradient text-white`}><Share className="w-4 h-4" /> So geht's (3 Schritte)</Link>;
  if (d.kind === "ios-inapp") return <button onClick={copyLink} data-testid="pushcard-copy-link" className={`${cta} pink-gradient text-white`}><Copy className="w-4 h-4" /> Link kopieren</button>;
  if (d.kind === "android-inapp") return <a href={chromeIntent()} data-testid="pushcard-open-chrome" className={`${cta} pink-gradient text-white`}><ExternalLink className="w-4 h-4" /> In Chrome öffnen</a>;
  if (d.kind === "denied") return <Link to="/push" data-testid="pushcard-denied-help" className={`${cta} bg-slate-100 text-slate-700`}><Settings className="w-4 h-4" /> Anleitung</Link>;
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={onEnable} disabled={busy} data-testid="pushcard-enable" className={`${cta} pink-gradient text-white disabled:opacity-60`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Push aktivieren</button>
      {installable && <button onClick={onInstall} data-testid="pushcard-install" className={`${cta} bg-white border border-pink-200 text-rose-600`}><Download className="w-4 h-4" /> App installieren</button>}
    </div>
  );
}

export function PushCard() {
  const [d] = useState(detectDevice);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installable, setInstallable] = useState(canInstall());
  useEffect(() => {
    if (snoozed() || d.kind === "unsupported") return undefined;
    getSubscription().then((s) => setShow(!s)).catch(() => setShow(true));
    const on = () => setInstallable(true);
    window.addEventListener("install-available", on);
    return () => window.removeEventListener("install-available", on);
  }, [d.kind]);
  if (!show) return null;

  const later = () => { localStorage.setItem(KEY, String(Date.now() + SNOOZE_DAYS * 86400000)); setShow(false); };
  const onEnable = async () => {
    setBusy(true);
    try { await enablePush(); toast.success("Push aktiviert! 🔔"); setShow(false); }
    catch (e) { toast.error(e.message || "Aktivieren fehlgeschlagen"); } finally { setBusy(false); }
  };
  const onInstall = async () => { if (await promptInstall()) toast.success("itzPlay wird installiert 🎉"); setInstallable(false); };
  const [title, text] = TEXT[d.kind];
  const Icon = d.os === "desktop" ? Monitor : Smartphone;

  return (
    <div className="relative rounded-3xl p-4 sm:p-5 bg-white border-2 border-rose-200 shadow-lg shadow-rose-100 fade-up" data-testid="push-card" data-kind={d.kind}>
      <button onClick={later} data-testid="pushcard-later" className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-xs font-semibold text-slate-500 hover:bg-slate-100"><X className="w-3.5 h-3.5" /> Später</button>
      <div className="flex gap-3 sm:gap-4 pr-16">
        <span className="w-11 h-11 shrink-0 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center"><Icon className="w-5 h-5" /></span>
        <div className="min-w-0 space-y-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-rose-500" data-testid="pushcard-device">Erkannt: {d.label}{d.standalone ? " · App" : ""}</p>
          <p className="font-bold leading-snug">{title}</p>
          <p className="text-sm text-slate-600">{text}</p>
        </div>
      </div>
      <div className="mt-4"><Actions d={d} busy={busy} onEnable={onEnable} installable={installable} onInstall={onInstall} /></div>
    </div>
  );
}
